import { stringToBoolean } from '@mvecom/common/utils/stringToBoolean';
import { jwtVerify, createRemoteJWKSet, decodeJwt, JWTPayload } from 'jose';
import Cookies from 'js-cookie';

import { Actions } from '../actions/enums';
import { setupSession } from '../network/axios';
import { getVerifiedAuthStatus } from '../network/endpoints/profile-pages/getVerifiedAuthStatus';
import { Cookie } from '../network/enums';
import { Authentication } from '../network/types';
import { getCookieDomain } from '../network/utils/getCookieDomain';
import { getRetailUnitAndLanguage } from '../network/utils/getRetailUnitAndLanguage';
import { getRetailUnitCookiePath } from '../network/utils/getRetailUnitCookiePath';
import { getAuthTokenCookieName } from './getAuthCookieName';
import { isCiamEnabled, isGuestTokenEnabled } from './retailUnitsWithCiam';
import { getAuthServiceUrl, getGuestServiceUrl, getGuestJwksUrl } from './url';

const { retailUnit, language } = getRetailUnitAndLanguage();
const GUEST_COOKIE = 'guest';

// JWKS endpoint for guest token verification (environment-aware)
const JWKS = createRemoteJWKSet(new URL(getGuestJwksUrl()));

function getGuestTokenFromCookie(): string | undefined {
  return Cookies.get(GUEST_COOKIE);
}

async function verifyGuestToken(token: string): Promise<JWTPayload | undefined> {
  try {
    const { payload } = await jwtVerify(token, JWKS);
    return payload;
  } catch {
    return undefined;
  }
}

function isTokenExpired(payload: JWTPayload) {
  const exp = (payload.exp ?? 0) * 1000;
  return new Date(exp) < new Date();
}

function isTokenAboutToExpire(payload: JWTPayload) {
  const fifteenMinutes = 15 * 60 * 1000;
  return (payload.exp ?? 0) * 1000 - Date.now() < fifteenMinutes;
}

export function hasWCCookies(): boolean {
  const all = Cookies.get();
  return Object.keys(all ?? {}).some((key) => key.startsWith('WC_'));
}

export function hasGuestCookie(): boolean {
  return Boolean(getGuestTokenFromCookie());
}

export function authHelper(): typeof globalThis.ikea.mvAgent.authentication {
  // Only one simultaneous isLoggedIn call is permitted
  let isLoggedInPromise: Promise<Authentication>;
  let pending = false;
  const provider = isCiamEnabled ? 'ciam' : 'icaps';

  const authCookie = getAuthTokenCookieName(retailUnit);
  let tokenRefresh: Promise<{ value?: string; expirationTime?: number }> | undefined;

  function readAuthToken(): { value?: string; expirationTime?: number } {
    const value = Cookies.get(authCookie);
    try {
      return value ? { value, expirationTime: decodeJwt(value).exp } : {};
    } catch {
      return {};
    }
  }

  async function getAuthToken(sessionVerified = false): Promise<{ value?: string; expirationTime?: number }> {
    if (!isCiamEnabled) return {};
    const token = readAuthToken();
    if (!token.value) return {};
    const remaining = (token.expirationTime ?? 0) * 1000 - Date.now();
    if (remaining >= 15 * 60 * 1000) return token;

    tokenRefresh ??= (async () => {
      if (remaining <= 0) {
        if (sessionVerified) return {};
        try {
          const status = await getVerifiedAuthStatus(getAuthServiceUrl(), retailUnit, language);
          if (!status.isAuthenticated) return {};
        } catch {
          // An unavailable customer session must not discard usable guest credentials.
          return {};
        }
      } else {
        try {
          const response = await fetch(`${getAuthServiceUrl()}/refresh/`, { credentials: 'include' });
          if (!response.ok) throw new Error(`Authentication refresh failed: ${response.status}`);
        } catch {
          // A proactive refresh must not block commerce while the cookie is still valid.
          // Read it again in case it expired or was cleared during the request.
          const current = readAuthToken();
          if (current.value && (current.expirationTime ?? 0) * 1000 > Date.now()) return current;
          return {};
        }
      }
      const updated = readAuthToken();
      return (updated.expirationTime ?? 0) * 1000 > Date.now() ? updated : {};
    })().finally(() => {
      tokenRefresh = undefined;
    });
    return tokenRefresh;
  }

  // Guest token state
  let guestToken: string | undefined;
  let guestTokenPromise: Promise<string> | null = null;

  async function fetchNewGuestToken(): Promise<string> {
    const res = await fetch(`${getGuestServiceUrl()}/token/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ retailUnit }),
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch guest token: ${res.status}`);
    }

    const data = await res.json();

    return data.access_token;
  }

  async function refreshGuestToken(token: string): Promise<string> {
    const res = await fetch(`${getGuestServiceUrl()}/refreshtoken/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ token }),
    });

    if (!res.ok) {
      throw new Error(`Failed to refresh guest token: ${res.status}`);
    }

    const data = await res.json();

    return data.access_token;
  }

  async function getOrRefreshGuestToken(): Promise<string> {
    guestTokenPromise ??= (async () => {
      try {
        guestToken ??= getGuestTokenFromCookie();

        if (guestToken) {
          const payload = await verifyGuestToken(guestToken);
          if (!payload || isTokenExpired(payload)) {
            guestToken = await fetchNewGuestToken();
          } else if (isTokenAboutToExpire(payload)) {
            guestToken = await refreshGuestToken(guestToken);
          }
        } else {
          guestToken = await fetchNewGuestToken();
        }

        if (!guestToken) {
          throw new Error('Guest token is undefined after fetch');
        }

        return guestToken;
      } catch (error) {
        console.error('Failed to get or refresh guest token:', error);
        throw error;
      } finally {
        guestTokenPromise = null;
      }
    })();
    return guestTokenPromise;
  }

  const isLoggedIn = async (force = false): Promise<Authentication> => {
    if (!isCiamEnabled) {
      if (force || !Cookies.get(Cookie.USER_LOGGEDIN)) {
        await setupSession();
      }
      const isLoggedIn = stringToBoolean(Cookies.get(Cookie.USER_LOGGEDIN));
      return {
        isLoggedIn,
        authToken: undefined,
        guestToken: undefined,
        guestId: undefined,
        provider: 'icaps',
      };
    }

    let guest: string | undefined;
    let guestId: string | undefined;
    if (isGuestTokenEnabled && (!hasWCCookies() || hasGuestCookie())) {
      try {
        guest = await getOrRefreshGuestToken();
        if (guest) {
          const guestPayload = await verifyGuestToken(guest);
          guestId = guestPayload?.guestId as string | undefined;
        }
      } catch (error) {
        console.error('Failed to get guest token in isLoggedIn:', error);
        // Continue without guest token
      }
    }

    const { isAuthenticated } = await getVerifiedAuthStatus(
      getAuthServiceUrl(),
      retailUnit,
      language,
      undefined,
      force,
    );

    const wasAuthenticated = stringToBoolean(Cookies.get(Cookie.USER_LOGGEDIN));
    Cookies.set(Cookie.USER_LOGGEDIN, String(isAuthenticated), {
      domain: getCookieDomain,
      path: getRetailUnitCookiePath,
    });
    if (isAuthenticated && !wasAuthenticated) globalThis.ikea?.pubsub?.publish?.(Actions.USER_INFO_AVAILABLE);

    // CIAM markets without guest tokens (currently KW) still need WC guest
    // cookies for cart and favourites, including when returning after logout.
    if (
      !isAuthenticated &&
      !isGuestTokenEnabled &&
      (!Cookies.get(Cookie.USER_LOGGEDIN) ||
        !Cookies.get(Cookie.CART_GUEST_ID) ||
        !Cookies.get(Cookie.FAVOURITES_GUEST_ID))
    ) {
      await setupSession();
    }

    return {
      isLoggedIn: isAuthenticated,
      authToken: isAuthenticated ? (await getAuthToken(true)).value : undefined,
      guestToken: guest,
      guestId,
      provider,
    };
  };

  return {
    getTokens: async () => {
      let guest: { value: string; expirationTime?: number; userId?: string } | undefined;
      if (isGuestTokenEnabled && (!hasWCCookies() || hasGuestCookie())) {
        try {
          const token = await getOrRefreshGuestToken();
          if (token) {
            const payload = await verifyGuestToken(token);
            guest = {
              value: token,
              expirationTime: payload?.exp,
              userId: payload?.guestId as string | undefined,
            };
          }
        } catch (error) {
          console.error('Failed to get guest token in getTokens:', error);
          // Continue without guest token
        }
      }

      const auth = await getAuthToken();
      // CIAM cannot trust a stale logged-in flag when no customer token is usable.
      // ICAPS continues to determine its session from the WC login cookie.
      const needsWcGuest = isCiamEnabled
        ? !isGuestTokenEnabled && !auth.value
        : !stringToBoolean(Cookies.get(Cookie.USER_LOGGEDIN));

      // WC guests still need their commerce session cookies.
      if (
        needsWcGuest &&
        (!Cookies.get(Cookie.USER_LOGGEDIN) ||
          !Cookies.get(Cookie.CART_GUEST_ID) ||
          !Cookies.get(Cookie.FAVOURITES_GUEST_ID))
      ) {
        await setupSession();
      }

      return {
        auth: {
          value: undefined,
          expirationTime: undefined,
          ...auth,
        },
        ...(guest && { guest }),
      };
    },
    isLoggedIn: async (force = false) => {
      if (!pending || force) {
        isLoggedInPromise = isLoggedIn(force);
        pending = true;
      }
      const currentPromise = isLoggedInPromise;
      try {
        return await currentPromise;
      } finally {
        if (isLoggedInPromise === currentPromise) {
          pending = false;
        }
      }
    },
    meta: { isCiamEnabled, isGuestTokenEnabled },
  };
}
